import { useEffect, useState } from "react";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { LangProvider, useLang } from "@/components/landing/i18n";
import Nav from "@/components/landing/Nav";
import Hero from "@/components/landing/Hero";
import Marquee from "@/components/landing/Marquee";
import Manifesto from "@/components/landing/Manifesto";
import Spotlight from "@/components/landing/Spotlight";
import Steps from "@/components/landing/Steps";
import Features from "@/components/landing/Features";
import Testimonials from "@/components/landing/Testimonials";
import FAQ from "@/components/landing/FAQ";
import CTA from "@/components/landing/CTA";
import Footer from "@/components/landing/Footer";
import FullAssessment from "@/pages/FullAssessment";
import ResultCard from "@/pages/ResultCard";
import AdminPage from "@/pages/AdminPage";
import ResetPassword from "@/pages/ResetPassword";
import MyReports from "@/pages/MyReports";
import ComparePage from "@/pages/ComparePage";
import TeamPage from "@/pages/TeamPage";
import DashboardPage from "@/pages/DashboardPage";
import AdminCmsPage from "@/pages/AdminCmsPage";
import DiscoveryPage from "@/pages/DiscoveryPage";
import InstitutionPage from "@/pages/InstitutionPage";
import ProgressPage from "@/pages/ProgressPage";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";

const NOISE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E";

function Landing() {
  const [site, setSite] = useState(null);
  const { lang } = useLang();
  const navigate = useNavigate();
  const startQuiz = () => navigate("/assessment");

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 1 });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content/settings`)
      .then((r) => (r.ok ? r.json() : {}))
      .then(setSite)
      .catch(() => {});
  }, []);

  const sec = site?.sections || {};
  const show = (k) => sec[k] !== false;
  const showBar = Boolean(site?.announcement?.active && site.announcement.text);

  return (
    <div className="bg-paper text-ink font-body overflow-x-clip" data-testid="brightiq-landing">
      {showBar && (
        <div data-testid="announcement-bar" className="fixed top-0 inset-x-0 z-[60] h-9 bg-iqpink text-paper flex items-center justify-center text-xs sm:text-sm font-semibold tracking-wide px-4 text-center">
          {lang === "hi" ? "अर्ली एक्सेस अब खुला है — अपना आकलन शुरू करें" : site.announcement.text}
        </div>
      )}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-[90] opacity-[0.05] mix-blend-multiply"
        style={{ backgroundImage: `url("${NOISE}")` }}
      />
      <Nav onStart={startQuiz} offset={showBar} />
      <main>
        <Hero onStart={startQuiz} copy={site?.hero} />
        {show("marquee") && <Marquee />}
        {show("manifesto") && <Manifesto />}
        {show("spotlight") && <Spotlight />}
        {show("steps") && <Steps />}
        {show("features") && <Features />}
        {show("stories") && <Testimonials />}
        {show("faq") && <FAQ />}
        <CTA />
      </main>
      <Footer />
    </div>
  );
}

export default function App() {
  return (
    <LangProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/assessment" element={<FullAssessment />} />
          <Route path="/r/:id" element={<ResultCard />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/reports" element={<MyReports />} />
          <Route path="/compare" element={<ComparePage />} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/discovery" element={<DiscoveryPage />} />
          <Route path="/discovery/report/:reportId" element={<DiscoveryPage />} />
          <Route path="/progress" element={<ProgressPage />} />
          <Route path="/admin/cms" element={<AdminCmsPage />} />
          <Route path="/institution" element={<InstitutionPage />} />
        </Routes>
        <Toaster position="bottom-center" />
      </BrowserRouter>
    </LangProvider>
  );
}
